import {
  type DocumentContentSection,
  type DocumentData,
  type DocumentStep,
  type EmbeddingStatusResponse,
  type Project,
  type ReferenceDocument,
  type UserInfo,
  type VideoStorageInfo as DocumentStorageInfo,
  DocumentTypeTag,
  type PermissionExpression,
  type WhoAmIResponse,
  type DocumentVersionHistory,
  type OrgQuota,
  type Folder,
  type AvailableTenantEntry,
  type ProjectMember,
  type SearchResourceType,
  type SearchResponse,
  type ClipsResponse,
} from './entities'
import client, { WithSpan } from './client'
import { BASE_URL, BATCH_THUMBNAILS_MAX_ITEMS } from './constants'
import type { QueryClient } from '@tanstack/react-query'
import type { IdTokenResult, User } from 'firebase/auth'
import { getMutationAuthHeaders } from './mutations'
import { idTokenOptions, idTokenResultOptions } from './queryOptions'

// Centralized query keys
export const queryKeys = {
  auth: {
    idToken: (userId: string) => ['auth', 'idToken', userId] as const,
    idTokenResult: (userId: string) =>
      ['auth', 'idTokenResult', userId] as const,
  },
  tenants: {
    all: () => ['tenants'] as const,
    superAdmin: () => ['tenants', 'super-admin'] as const,
  },
  settings: {
    byName: <S>(name: Name<S>) => ['settings', name] as const,
  },
  orgPermissions: {
    currentUser: (tenantId: string) =>
      ['org-permissions', tenantId, 'current-user'] as const,
  },
  documents: {
    all: (
      tenantId: string,
      stage?: 'draft' | 'published' | 'pending_changes',
    ) =>
      stage
        ? (['documents', tenantId, stage] as const)
        : (['documents', tenantId] as const),
    byProject: (
      tenantId: string,
      projectId: string,
      stage?: 'draft' | 'published' | 'pending_changes',
      folder?: string,
    ) =>
      stage && folder
        ? ([
            'documents',
            tenantId,
            'project',
            projectId,
            stage,
            'folder',
            folder,
          ] as const)
        : stage
          ? (['documents', tenantId, 'project', projectId, stage] as const)
          : folder
            ? ([
                'documents',
                tenantId,
                'project',
                projectId,
                'folder',
                folder,
              ] as const)
            : (['documents', tenantId, 'project', projectId] as const),
    byId: (docId: string) => ['documents', 'id', docId] as const,
    byIdPermissions: (docId: string) =>
      ['documents', 'id', docId, 'permissions'] as const,
    createdByMe: ['documents', 'created-by-me'] as const,
    trash: ['documents', 'trash'] as const,
    recent: ['documents', 'recent'] as const,
    latestUpdates: ['documents', 'latest-updates'] as const,
    embeddingStatus: (docId: string) =>
      ['documents', docId, 'embedding-status'] as const,
    versionHistoryById: (docId: string) =>
      ['documents', docId, 'version-history'] as const,
    versionHistoryByVersion: (docId: string, version: number) =>
      ['documents', docId, 'version-history', version] as const,
    sopDiagnostics: (sopId: string) =>
      ['documents', 'id', sopId, 'diagnostics'] as const,
  },
  referenceDocuments: {
    all: (tenantId: string, projectId: string) =>
      ['reference-documents', tenantId, projectId] as const,
    byId: (referenceDocumentId: string) =>
      ['reference-document', referenceDocumentId] as const,
    byIds: (referenceDocumentIds: string[]) =>
      ['reference-documents', 'ids', ...referenceDocumentIds] as const,
  },
  projects: {
    all: (tenantId: string) => ['projects', tenantId] as const,
    byId: (projectId: string) => ['projects', 'id', projectId] as const,
    members: (tenantId: string, projectId: string) =>
      ['projects', tenantId, projectId, 'members'] as const,
  },
  folders: {
    byProject: (tenantId: string, projectId: string) =>
      ['folders', tenantId, projectId] as const,
    byName: (tenantId: string, projectId: string, name: string) =>
      ['folders', tenantId, projectId, name] as const,
    byProjects: (tenantId: string, projectIds: string[]) =>
      ['folders', tenantId, 'batch', ...projectIds.sort()] as const,
  },
  images: {
    byUrl: (url: string) => ['images', url] as const,
  },
  users: {
    all: (tenantId: string) => ['users', tenantId] as const,
    me: (tenantId: string) => ['users', tenantId, 'me'] as const,
    whoami: () => ['users', 'whoami'] as const,
    meProfile: (tenantId: string) =>
      ['users', tenantId, 'me', 'profile'] as const,
    byIdProfile: (userId: string) => ['users', userId, 'profile'] as const,
    byIdIsOrgAdmin: (userId: string) =>
      ['users', userId, 'isOrgAdmin'] as const,
    byIdIsOrgEditor: (userId: string) =>
      ['users', userId, 'isOrgEditor'] as const,
  },
  bookmarks: {
    all: (tenantId: string) => ['bookmarks', tenantId] as const,
    combined: (tenantId: string) =>
      ['bookmarks', tenantId, 'combined'] as const,
    sops: (tenantId: string) => ['bookmarks', tenantId, 'sops'] as const,
    documents: (tenantId: string) =>
      ['bookmarks', tenantId, 'documents'] as const,
  },
  org: {
    quotas: (tenantId: string) => ['org', tenantId, 'quotas'] as const,
  },
  copy: {
    availableTenants: (tenantId: string) =>
      ['copy', 'available-tenants', tenantId] as const,
  },
  search: {
    byQuery: (tenantId: string, query: string, types?: SearchResourceType[]) =>
      types && types.length > 0
        ? (['search', tenantId, query, ...types] as const)
        : (['search', tenantId, query] as const),
  },
  clips: {
    byDocument: (tenantId: string, projectId: string, documentId: string) =>
      ['clips', tenantId, projectId, documentId] as const,
  },
} as const

interface GetIdTokenParams {
  queryClient: QueryClient
  authState: User
}

// Note: recommend only using this function to get token id for fetch requests. For mutations, use the authState object to get a fresh token.
async function getIdTokenUsingQueryClientInternal({
  queryClient,
  authState,
}: GetIdTokenParams): Promise<string> {
  const idToken = await queryClient.ensureQueryData(
    idTokenOptions(authState.uid, () => authState.getIdToken()),
  )
  return idToken
}

export const getIdTokenUsingQueryClient = WithSpan(
  'getIdTokenUsingQueryClient',
  getIdTokenUsingQueryClientInternal,
)

export const getIdTokenResultUsingQueryClient = WithSpan(
  'getIdTokenResultUsingQueryClient',
  getIdTokenResultUsingQueryClientInternal,
)

async function getIdTokenResultUsingQueryClientInternal({
  queryClient,
  authState,
}: GetIdTokenParams): Promise<IdTokenResult> {
  const idTokenResult = await queryClient.ensureQueryData(
    idTokenResultOptions(authState.uid, () => authState.getIdTokenResult()),
  )
  return idTokenResult
}

interface GetTenantProjectByIdParams {
  token: string
  tenantId: string
  projectId: string
}

export const getTenantProjectById = WithSpan(
  'getTenantProjectById',
  getTenantProjectByIdInternal,
)

async function getTenantProjectByIdInternal({
  token,
  tenantId,
  projectId,
}: GetTenantProjectByIdParams): Promise<Project> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  )
  return response.data
}

interface GetUserByIdParams {
  token: string
  tenantId: string
  userId: string
}

export const getUserById = WithSpan('getUserById', getUserByIdInternal)

async function getUserByIdInternal({
  token,
  tenantId,
  userId,
}: GetUserByIdParams): Promise<UserInfo | null> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/users/${userId}/profile`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error fetching user by id:', error)
    return null
  }
}

interface GetTenantProjectsParams {
  token: string
  tenantId: string
}

export const getTenantProjects = getTenantProjectsInternal
// WithSpan('getTenantProjects', getTenantProjectsInternal)

async function getTenantProjectsInternal({
  token,
  tenantId,
}: GetTenantProjectsParams): Promise<Project[] | null> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/projects`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    )
    return response.data?.sort((a: Project, b: Project) =>
      a.name.localeCompare(b.name),
    )
  } catch (error) {
    console.error('Error fetching projects:', error)
    return null
  }
}

interface GetProjectMembersParams {
  token: string
  tenantId: string
  projectId: string
}

export const getProjectMembers = WithSpan(
  'getProjectMembers',
  getProjectMembersInternal,
)

async function getProjectMembersInternal({
  token,
  tenantId,
  projectId,
}: GetProjectMembersParams): Promise<ProjectMember[]> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/members`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  )
  return (response.data as ProjectMember[]).map((m) => ({
    ...m,
    role: m.role.toUpperCase(),
  }))
}

interface GetDocumentsParams {
  token: string
  tenantId: string
  projectId?: string
  stage?: 'draft' | 'published' | 'pending_changes'
  folder?: string
  offset?: number
  limit?: number
}

export interface PaginatedDocuments {
  data: DocumentData[]
  total: number
}

export const getDocuments = WithSpan('getDocuments', getDocumentsInternal)

/** Fetch a single page of SOPs from the tenant-wide endpoint. */
async function getDocumentsInternal({
  token,
  tenantId,
  stage,
  offset = 0,
  limit = 50,
}: GetDocumentsParams): Promise<PaginatedDocuments> {
  const headers = { Authorization: `Bearer ${token}` }

  const response = await client.get(`${BASE_URL}/api/org/${tenantId}/sops`, {
    headers,
    params: { ...(stage ? { stage } : {}), offset, limit },
  })

  return {
    data: response.data.data ?? response.data,
    total: response.data.total ?? response.data.length,
  }
}

export const getDocumentsInProject = getDocumentsInProjectInternal
// WithSpan('getDocumentsInProject', getDocumentsInProjectInternal)

function normalizeDocumentFolder(folder?: string | null): string {
  return folder && folder !== 'root' ? folder : 'root'
}

async function getDocumentsInProjectInternal({
  token,
  tenantId,
  projectId,
  stage,
  folder,
}: GetDocumentsParams): Promise<DocumentData[]> {
  const params: Record<string, string> = {}
  if (stage) params.stage = stage
  if (folder) params.folder = folder

  // Fetch both SOPs and reference documents (PDFs) in parallel
  const [sopsResponse, refDocsResponse] = await Promise.all([
    client
      .get(`${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: Object.keys(params).length > 0 ? params : undefined,
      })
      .catch(() => ({ data: [] })), // Gracefully handle failures
    client
      .get(`${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { collection: 'documents' },
      })
      .catch(() => ({ data: [] })), // Gracefully handle failures
  ])

  const sops: DocumentData[] = sopsResponse.data || []
  const refDocs: ReferenceDocument[] = refDocsResponse.data || []
  const filteredRefDocs = folder
    ? refDocs.filter(
        (doc) =>
          normalizeDocumentFolder(doc.folder) ===
          normalizeDocumentFolder(folder),
      )
    : refDocs

  // Convert PDF reference documents to DocumentData format for unified display
  const pdfDocuments: DocumentData[] = filteredRefDocs
    .filter((doc) => doc.metadata?.mimeType === 'application/pdf')
    .map((doc) => ({
      id: doc.id,
      title: doc.metadata.filename,
      creator: doc.creator,
      status: 'COMPLETED' as const,
      projectId: doc.projectId,
      content: { sections: [], steps: [] },
      documentRefs: [
        {
          id: doc.id,
          displayName: doc.metadata.filename,
          mimeType: doc.metadata.mimeType || 'application/pdf',
          documentType: 'PDF',
        },
      ],
      metadata: {
        type: DocumentTypeTag.PDF,
      },
      currentVersion: 1,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      rag: doc.rag,
      folder: doc.folder,
    }))

  // Combine SOPs and PDF documents
  return [...sops, ...pdfDocuments]
}

export const getReferenceDocumentsInProject =
  getReferenceDocumentsInProjectInternal
// WithSpan('getReferenceDocumentsInProject', getReferenceDocumentsInProjectInternal)

async function getReferenceDocumentsInProjectInternal({
  token,
  tenantId,
  projectId,
}: GetDocumentsParams): Promise<ReferenceDocument[]> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return response.data
}

interface GetMyDocumentsParams {
  token: string
  tenantId: string
  userId: string
}

export const getDocumentsCreatedByMe = WithSpan(
  'getDocumentsCreatedByMe',
  getDocumentsCreatedByMeInternal,
)

async function getDocumentsCreatedByMeInternal({
  token,
  tenantId,
  userId,
}: GetMyDocumentsParams): Promise<DocumentData[]> {
  // Fetch first page — created-by-me filtering is done client-side
  // TODO: add server-side creator filter when available
  const result = await getDocuments({ token, tenantId, offset: 0, limit: 100 })
  return result.data.filter((document) => document.creator === userId)
}

interface GetRecentDocumentsParams {
  token: string
  tenantId: string
}

export const getRecentDocuments = WithSpan(
  'getRecentDocuments',
  getRecentDocumentsInternal,
)

async function getRecentDocumentsInternal({
  tenantId,
  token,
}: GetRecentDocumentsParams): Promise<DocumentData[]> {
  const result = await getDocuments({ token, tenantId, offset: 0, limit: 3 })
  return result.data
}

export const getLatestUpdates = WithSpan(
  'getLatestUpdates',
  getLatestUpdatesInternal,
)

async function getLatestUpdatesInternal({
  tenantId,
  token,
}: GetRecentDocumentsParams): Promise<DocumentData[]> {
  const result = await getDocuments({ token, tenantId, offset: 0, limit: 3 })
  return result.data
}

export const getTrashDocuments = WithSpan(
  'getTrashDocuments',
  getTrashDocumentsInternal,
)

async function getTrashDocumentsInternal({
  token,
  tenantId,
}: GetDocumentsParams): Promise<DocumentData[]> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/sops/trash`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return response.data
}

interface GetDocumentByIdParams {
  token: string
  tenantId: string
  projectId: string
  docId: string
}

export const getDocumentDataById = getDocumentDataByIdInternal
// WithSpan('getDocumentDataById', getDocumentDataByIdInternal)

async function getDocumentDataByIdInternal({
  token,
  tenantId,
  projectId,
  docId,
}: GetDocumentByIdParams): Promise<DocumentData> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${docId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  // Handle case where content might be undefined
  const content = response.data.content || {}

  // Handle case where steps might be undefined
  const steps = content.steps || []
  const sortedSteps =
    steps.length > 0
      ? [...steps].sort((a: DocumentStep, b: DocumentStep) => a.order - b.order)
      : []

  // Handle case where sections might be undefined
  const sections = content.sections || []
  const sortedSections =
    sections.length > 0
      ? [...sections].sort(
          (a: DocumentContentSection, b: DocumentContentSection) =>
            a.order - b.order,
        )
      : []

  return {
    ...response.data,
    content: {
      ...content,
      steps: sortedSteps,
      sections: sortedSections,
    },
  }
}

interface GetDocumentEmbeddingStatusParams {
  token: string
  tenantId: string
  projectId: string
  docId: string
}

async function getDocumentEmbeddingStatusInternal({
  token,
  tenantId,
  projectId,
  docId,
}: GetDocumentEmbeddingStatusParams): Promise<EmbeddingStatusResponse> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${docId}/embeddings/status`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return response.data
}

export const getDocumentEmbeddingStatus = WithSpan(
  'getDocumentEmbeddingStatus',
  getDocumentEmbeddingStatusInternal,
)

// SOP diagnostics — dev-only tab on the SOP edit page. The backend response
// shape is defined in linewise-api SOPModels.scala (`SOPDiagnosticsResponse`,
// `SOPDiagnostics`). The two `Option` fields (`diagnostics`, `errorMessage`,
// etc.) are surfaced as optional members here.
export interface SOPDiagnosticsPayload {
  rawOutput?: string | null
  prompt?: string | null
  // Backend uses `GeminiModelConfig`; the frontend treats it as opaque JSON
  // because the only consumer is a JSON viewer.
  modelConfig?: unknown
}

export interface SOPDiagnosticsResponse {
  id: string
  title: string
  status: string
  stage?: string | null
  hasContent: boolean
  hasPublished: boolean
  currentVersion: number
  documentCount: number
  availableLocales: string[]
  errorMessage?: string | null
  diagnostics?: SOPDiagnosticsPayload | null
  createdAt: string
  updatedAt: string
}

interface GetSOPDiagnosticsParams {
  token: string
  tenantId: string
  projectId: string
  sopId: string
}

async function getSOPDiagnosticsInternal({
  token,
  tenantId,
  projectId,
  sopId,
}: GetSOPDiagnosticsParams): Promise<SOPDiagnosticsResponse> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}/diagnostics`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return response.data
}

export const getSOPDiagnostics = WithSpan(
  'getSOPDiagnostics',
  getSOPDiagnosticsInternal,
)

interface GetDocumentVersionHistoryByIdParams {
  token: string
  tenantId: string
  projectId: string
  documentId: string
}

export const getDocumentVersionHistoryById = WithSpan(
  'getDocumentVersionHistoryById',
  getDocumentVersionHistoryByIdInternal,
)

async function getDocumentVersionHistoryByIdInternal({
  token,
  tenantId,
  projectId,
  documentId,
}: GetDocumentVersionHistoryByIdParams): Promise<DocumentVersionHistory[]> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${documentId}/versions`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return response.data
}

interface GetDocumentVersionHistoryByVersionParams {
  token: string
  tenantId: string
  projectId: string
  documentId: string
  version: number
}

export const getDocumentVersionHistoryByVersion = WithSpan(
  'getDocumentVersionHistoryByVersion',
  getDocumentVersionHistoryByVersionInternal,
)

async function getDocumentVersionHistoryByVersionInternal({
  token,
  tenantId,
  projectId,
  documentId,
  version,
}: GetDocumentVersionHistoryByVersionParams): Promise<DocumentData> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${documentId}/versions/${version}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  // Handle case where content might be undefined
  const content = response.data.content || {}

  // Handle case where steps might be undefined
  const steps = content.steps || []
  const sortedSteps =
    steps.length > 0
      ? [...steps].sort((a: DocumentStep, b: DocumentStep) => a.order - b.order)
      : []

  // Handle case where sections might be undefined
  const sections = content.sections || []
  const sortedSections =
    sections.length > 0
      ? [...sections].sort(
          (a: DocumentContentSection, b: DocumentContentSection) =>
            a.order - b.order,
        )
      : []

  return {
    ...response.data,
    content: {
      ...content,
      steps: sortedSteps,
      sections: sortedSections,
    },
  }
}

interface GetReferenceDocumentParams {
  token: string
  tenantId: string
  projectId: string
  referenceDocumentId: string
}

export const getReferenceDocument = getReferenceDocumentInternal
// WithSpan('getReferenceDocument', getReferenceDocumentInternal)

async function getReferenceDocumentInternal({
  token,
  tenantId,
  projectId,
  referenceDocumentId,
}: GetReferenceDocumentParams): Promise<ReferenceDocument> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${referenceDocumentId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    return response.data
  } catch (error) {
    console.error('Get reference document error:', error)
    throw new Error('Failed to get reference document')
  }
}

interface GetReferenceDocumentsByIDsParams {
  token: string
  tenantId: string
  projectId: string
  referenceDocumentIds: string[]
}

export const getReferenceDocumentsByIDs = WithSpan(
  'getReferenceDocumentsByIDs',
  getReferenceDocumentsByIDsInternal,
)

async function getReferenceDocumentsByIDsInternal({
  token,
  tenantId,
  referenceDocumentIds,
}: GetReferenceDocumentsByIDsParams): Promise<ReferenceDocument[]> {
  if (referenceDocumentIds.length === 0) return []
  const headers = { Authorization: `Bearer ${token}` }
  const CHUNK_SIZE = 200
  // Chunk to stay within backend limit
  if (referenceDocumentIds.length <= CHUNK_SIZE) {
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/documents/batch`,
      { documentIds: referenceDocumentIds },
      { headers },
    )
    return response.data.documents
  }
  const results: ReferenceDocument[] = []
  for (let i = 0; i < referenceDocumentIds.length; i += CHUNK_SIZE) {
    const chunk = referenceDocumentIds.slice(i, i + CHUNK_SIZE)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/documents/batch`,
      { documentIds: chunk },
      { headers },
    )
    results.push(...response.data.documents)
  }
  return results
}

export const verifyDocumentUploadStatus = WithSpan(
  'verifyDocumentUploadStatus',
  verifyDocumentUploadStatusInternal,
)

async function verifyDocumentUploadStatusInternal({
  token,
  tenantId,
  projectId,
  referenceDocumentId,
}: GetReferenceDocumentParams): Promise<boolean> {
  const maxAttempts = 5
  const initialBackoffMs = 1_000
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const doc = await getReferenceDocument({
      token,
      tenantId,
      projectId,
      referenceDocumentId,
    })
    if (!!doc.gcsUri) {
      return true
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((r) =>
        setTimeout(r, initialBackoffMs * 2 ** attempt),
      )
    }
  }
  return false
}

export const getPDFDownloadUrl = WithSpan(
  'getPDFDownloadUrl',
  getPDFDownloadUrlInternal,
)

async function getPDFDownloadUrlInternal({
  token,
  tenantId,
  projectId,
  referenceDocumentId,
}: GetReferenceDocumentParams): Promise<string> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${referenceDocumentId}/download-url`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    return response.data.url
  } catch (error) {
    console.error('Get PDF download URL error:', error)
    throw new Error('Failed to get PDF download URL')
  }
}

interface GetUserMeParams {
  token: string
  tenantId: string
}

export const getUserMe = WithSpan('getUserMe', getUserMeInternal)

async function getUserMeInternal({
  token,
  tenantId,
}: GetUserMeParams): Promise<UserInfo> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/users/me`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return response.data
}

export const getUserMeProfile = WithSpan(
  'getUserMeProfile',
  getUserMeProfileInternal,
)

async function getUserMeProfileInternal({
  token,
  tenantId,
}: GetUserMeParams): Promise<UserInfo> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/users/me/profile`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  )
  return response.data
}

interface GetUserInfoByIdParams {
  token: string
  tenantId: string
  userId: string
}

export const getUserInfoById = WithSpan(
  'getUserInfoById',
  getUserInfoByIdInternal,
)

async function getUserInfoByIdInternal({
  token,
  tenantId,
  userId,
}: GetUserInfoByIdParams): Promise<UserInfo | null> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/users/${userId}/profile`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  } catch (error) {
    console.warn('Error fetching user info by id:', error)
    return null
  }
}

interface GetDocumentDownloadUrlParams {
  token: string
  tenantId: string
  projectId: string
  source: string
}

export const getDocumentDownloadUrl = WithSpan(
  'getDocumentDownloadUrl',
  getDocumentDownloadUrlInternal,
)

async function getDocumentDownloadUrlInternal({
  token,
  tenantId,
  projectId,
  source,
}: GetDocumentDownloadUrlParams): Promise<DocumentStorageInfo> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${source}/download-url`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )
  return response.data
}

interface GetVideoThumbnailParams {
  token: string
  tenantId: string
  projectId: string
  source: string
  position: number
  resolution?: 'SD' | 'HD' | 'FHD' | 'Original'
  quality?: number
}

export const getVideoThumbnail = WithSpan(
  'getVideoThumbnail',
  getVideoThumbnailInternal,
)

async function getVideoThumbnailInternal({
  token,
  tenantId,
  projectId,
  source,
  position,
  resolution = 'SD',
  quality = 80,
}: GetVideoThumbnailParams): Promise<string> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${source}/thumbnail`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        position,
        size: resolution,
        quality,
      },
    },
  )
  return response.data.url
}

interface CheckPermissionParams {
  authState: User
  tenantId: string
  projectId: string
  docId: string
  role: 'admin' | 'editor' | 'viewer'
}

export const checkUserPermissionOnSOP = checkUserPermissionOnSOPInternal
// WithSpan('checkUserPermissionOnSOP', checkUserPermissionOnSOPInternal)

async function checkUserPermissionOnSOPInternal({
  authState,
  tenantId,
  projectId,
  docId,
  role,
}: CheckPermissionParams): Promise<boolean> {
  const headers = await getMutationAuthHeaders(authState)
  const response = await client.post(
    `${BASE_URL}/api/org/${tenantId}/permissions/check`,
    {
      resourcePath: `/org/${tenantId}/projects/${projectId}/sops/${docId}`,
      role,
    },
    {
      headers,
      withCredentials: true,
    },
  )

  return response.data.hasPermission
}

interface ListOrgUsersParams {
  token: string
  tenantId: string
}

export const listOrgUsers = WithSpan('listOrgUsers', listOrgUsersInternal)

async function listOrgUsersInternal({
  token,
  tenantId,
}: ListOrgUsersParams): Promise<UserInfo[]> {
  const response = await client.get(`${BASE_URL}/api/org/${tenantId}/users`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  })
  return response.data
}

interface GetAllBookmarksParams {
  token: string
  tenantId: string
}

type BookmarkResponse = {
  type: string
  id: string
  resourceId: string
  createdAt: string
  sop?: DocumentData
  document?: {
    id: string
    projectId: string
    metadata: {
      filename: string
      mimeType: string
      size: number
    }
    content: string
    contentSize: number
    creator: string
    createdAt: string
    updatedAt: string
    gcsUri?: string
    folder?: string
    rag?: {
      status: 'PENDING' | 'PROCESSING' | 'SYNCED' | 'FAILED'
      error?: string
      lastSyncAt?: string
    } | null
  }
}

export const getAllBookmarks = WithSpan(
  'getAllBookmarks',
  getAllBookmarksInternal,
)

async function getAllBookmarksInternal({
  token,
  tenantId,
}: GetAllBookmarksParams): Promise<{
  sops: DocumentData[]
  documents: DocumentData[]
}> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/bookmarks`,
    {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    },
  )

  const sops: DocumentData[] = []
  const documents: DocumentData[] = []

  response.data.forEach((bookmark: BookmarkResponse) => {
    if (
      bookmark.type === 'io.linewise.features.bookmark.SOPBookmarkResponse' &&
      bookmark.sop
    ) {
      sops.push(bookmark.sop)
    } else if (
      bookmark.type ===
        'io.linewise.features.bookmark.DocumentBookmarkResponse' &&
      bookmark.document
    ) {
      // Transform document to DocumentData format
      const doc = bookmark.document
      documents.push({
        id: doc.id,
        title: doc.metadata.filename,
        creator: doc.creator,
        status: 'COMPLETED',
        projectId: doc.projectId,
        metadata: {
          type: DocumentTypeTag.PDF,
          filename: doc.metadata.filename,
          mimeType: doc.metadata.mimeType,
          size: doc.metadata.size,
        },
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
        rag: doc.rag ?? null,
      })
    }
  })

  return { sops, documents }
}

// Legacy functions for backward compatibility - now use getAllBookmarks internally
interface GetBookmarkedSOPsParams {
  token: string
  tenantId: string
}

export const getBookmarkedSOPs = WithSpan(
  'getBookmarkedSOPs',
  getBookmarkedSOPsInternal,
)

async function getBookmarkedSOPsInternal({
  token,
  tenantId,
}: GetBookmarkedSOPsParams): Promise<DocumentData[]> {
  const result = await getAllBookmarksInternal({ token, tenantId })
  return result.sops
}

export const getBookmarkedDocuments = WithSpan(
  'getBookmarkedDocuments',
  getBookmarkedDocumentsInternal,
)

async function getBookmarkedDocumentsInternal({
  token,
  tenantId,
}: GetBookmarkedSOPsParams): Promise<DocumentData[]> {
  const result = await getAllBookmarksInternal({ token, tenantId })
  return result.documents
}

interface GetOrgPermissionsCurrentUserParams {
  token: string
  tenantId: string
}

export const getOrgPermissionsCurrentUser = WithSpan(
  'getOrgPermissionsCurrentUser',
  getOrgPermissionsCurrentUserInternal,
)

async function getOrgPermissionsCurrentUserInternal({
  token,
  tenantId,
}: GetOrgPermissionsCurrentUserParams): Promise<PermissionExpression> {
  const response = await client.get(
    `${BASE_URL}/api/org/${tenantId}/permissions`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  )

  if (!response?.data) {
    throw new Error(
      `Failed to fetch org permissions: response data is undefined for tenant ${tenantId}`,
    )
  }

  const orgPermissions = response.data as PermissionExpression

  return orgPermissions
}

interface GetAvailableTenantsForSuperAdminParams {
  token: string
}

export interface Tenant {
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

export async function getAvailableTenantsForSuperAdmin({
  token,
}: GetAvailableTenantsForSuperAdminParams): Promise<Tenant[]> {
  const response = await client.get(`${BASE_URL}/api/system/tenants`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return response.data
}

interface GetWhoAmIParams {
  token: string
}

export async function getWhoAmI({
  token,
}: GetWhoAmIParams): Promise<WhoAmIResponse> {
  const response = await client.get<WhoAmIResponse>(
    `${BASE_URL}/api/users/whoami`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  )
  return response.data
}

export type Setting<N extends string, T> = {
  name: N
  data: T
}

export type DefaultLanguage = Setting<'default-language', { language: string }>
export type Chat = Setting<'chat', { enable: boolean }>
export type SopGeneratePrompt = Setting<
  'sop-generate-prompt',
  { prompt: string }
>

export type Logo = Setting<'logo', { logo: string }>

export type SopPdfTemplate = Setting<
  'sop-pdf-template',
  { template: 'default' | 'TWI' | 'TWI-hesai' | 'flex' | null }
>

interface GetSettingsParams<S> {
  token: string
  tenantId: string
  name: Name<S>
}

export type Name<S> = S extends Setting<infer N, any> ? N : never
export type Data<S> = S extends Setting<any, infer D> ? D : never

export async function getSettings<S>({
  token,
  tenantId,
  name,
}: GetSettingsParams<S>): Promise<Data<S> | null> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/settings/${name}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    )
    return response.data
  } catch (error) {
    console.warn('Error getting settings:', error)
    return null
  }
}

interface GetOrgQuotasParams {
  token: string
  tenantId: string
}

export const getOrgQuotas = WithSpan('getOrgQuotas', getOrgQuotasInternal)

async function getOrgQuotasInternal({
  token,
  tenantId,
}: GetOrgQuotasParams): Promise<OrgQuota[]> {
  const response = await client.get(`${BASE_URL}/api/org/${tenantId}/quotas`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return response.data
}

interface GetFoldersParams {
  token: string
  tenantId: string
  projectId: string
}

export const getFolders = WithSpan('getFolders', getFoldersInternal)

async function getFoldersInternal({
  token,
  tenantId,
  projectId,
}: GetFoldersParams): Promise<Folder[]> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/folders`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error fetching folders:', error)
    return []
  }
}

interface GetAvailableTenantsForCopyParams {
  token: string
  tenantId: string
}

export const getAvailableTenantsForCopy = WithSpan(
  'getAvailableTenantsForCopy',
  getAvailableTenantsForCopyInternal,
)

async function getAvailableTenantsForCopyInternal({
  token,
  tenantId,
}: GetAvailableTenantsForCopyParams): Promise<AvailableTenantEntry[]> {
  try {
    const response = await client.get(
      `${BASE_URL}/api/org/${tenantId}/copy/available-tenants`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    )
    return response.data
  } catch (error) {
    console.error('Get available tenants for copy error:', error)
    throw new Error('Failed to get available tenants for copy')
  }
}

// --- Batch API functions ---

interface GetBatchFoldersParams {
  token: string
  tenantId: string
  projectIds: string[]
}

export const getBatchFolders = WithSpan(
  'getBatchFolders',
  getBatchFoldersInternal,
)

async function getBatchFoldersInternal({
  token,
  tenantId,
  projectIds,
}: GetBatchFoldersParams): Promise<Record<string, Folder[]>> {
  if (projectIds.length === 0) return {}
  const response = await client.post(
    `${BASE_URL}/api/org/${tenantId}/batch/folders`,
    { projectIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  )
  return response.data.folders
}

export interface BatchThumbnailItem {
  documentId: string
  position: number
  size?: string
  quality?: number
}

export interface BatchThumbnailResult {
  url?: string
  status: string
  error?: string
}

interface GetBatchThumbnailsParams {
  token: string
  tenantId: string
  projectId: string
  items: BatchThumbnailItem[]
}

export const getBatchThumbnails = WithSpan(
  'getBatchThumbnails',
  getBatchThumbnailsInternal,
)

async function getBatchThumbnailsInternal({
  token,
  tenantId,
  projectId,
  items,
}: GetBatchThumbnailsParams): Promise<Record<string, BatchThumbnailResult>> {
  if (items.length === 0) return {}
  const url = `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/batch/thumbnails`
  const headers = { Authorization: `Bearer ${token}` }

  if (items.length <= BATCH_THUMBNAILS_MAX_ITEMS) {
    const response = await client.post(url, { items }, { headers })
    return response.data.results
  }

  // Server caps batch size per request. Fan out into parallel chunks so
  // callers can pass any number of items without violating the contract.
  const chunks: BatchThumbnailItem[][] = []
  for (let i = 0; i < items.length; i += BATCH_THUMBNAILS_MAX_ITEMS) {
    chunks.push(items.slice(i, i + BATCH_THUMBNAILS_MAX_ITEMS))
  }
  const responses = await Promise.all(
    chunks.map((chunk) =>
      client
        .post(url, { items: chunk }, { headers })
        .then((r) => r.data.results),
    ),
  )
  return Object.assign(
    {} as Record<string, BatchThumbnailResult>,
    ...responses,
  )
}

interface GetBatchEmbeddingsStatusParams {
  token: string
  tenantId: string
  documentIds: string[]
}

export const getBatchEmbeddingsStatus = WithSpan(
  'getBatchEmbeddingsStatus',
  getBatchEmbeddingsStatusInternal,
)

async function getBatchEmbeddingsStatusInternal({
  token,
  tenantId,
  documentIds,
}: GetBatchEmbeddingsStatusParams): Promise<
  Record<string, EmbeddingStatusResponse>
> {
  if (documentIds.length === 0) return {}
  const response = await client.post(
    `${BASE_URL}/api/org/${tenantId}/documents/batch/embeddings-status`,
    { documentIds },
    { headers: { Authorization: `Bearer ${token}` } },
  )
  return response.data.statuses
}

// Universal search

interface UniversalSearchParams {
  token: string
  tenantId: string
  query: string
  types?: SearchResourceType[]
  limit?: number
}

export const universalSearch = WithSpan(
  'universalSearch',
  universalSearchInternal,
)

async function universalSearchInternal({
  token,
  tenantId,
  query,
  types,
  limit = 5,
}: UniversalSearchParams): Promise<SearchResponse> {
  const response = await client.post(
    `${BASE_URL}/api/org/${tenantId}/search`,
    { query, types, limit },
    { headers: { Authorization: `Bearer ${token}` } },
  )
  return response.data
}

// Video clips
//
// Note: the WebAuthn `listWebAuthnCredentials` helper that used to live
// here was removed; the credentials list is now fetched inside the
// Rust/Dioxus `WebAuthnCredentialList` component
// (`wasm/src/api/webauthn.rs:list_credentials`). The same is true for
// the register/authenticate/delete and MFA-reset endpoints that lived
// in `mutations.ts`.
interface GetDocumentClipsParams {
  token: string
  tenantId: string
  projectId: string
  documentId: string
}

export const getDocumentClips = WithSpan(
  'getDocumentClips',
  async ({
    token,
    tenantId,
    projectId,
    documentId,
  }: GetDocumentClipsParams): Promise<ClipsResponse> => {
    const response = await client.get<ClipsResponse>(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${documentId}/clips`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    )
    return response.data
  },
)
